import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  Database, Plus, Edit2, Trash2, Save, X, Search,
  Factory, Package, Truck, Warehouse, FolderKanban, Wrench, UserCog, Ship,
  RefreshCw, AlertCircle, CheckCircle, Layers, Download, Upload, Loader2
} from 'lucide-react';
import { CountryDropdown } from '@/components/CountryDropdown';
import RawMaterialConsumptionStock from '@/pages/raw-material/ConsumptionStock';
import { ProjectMasterData } from './ProjectMasterData';

// Department configuration
const departmentConfig = {
  production: {
    name: 'Production',
    icon: Factory,
    color: 'blue',
    tables: [
      { 
        id: 'furnaces', 
        name: 'Furnaces', 
        fields: [
          { name: 'furnaceNo', label: 'Furnace No', type: 'text', required: true },
          { name: 'capacity', label: 'Capacity (kg)', type: 'number' },
          { name: 'type', label: 'Type', type: 'select', options: ['Electric', 'Gas', 'Oil'] },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Maintenance', 'Inactive'] },
        ]
      },
      { 
        id: 'operators', 
        name: 'Operators', 
        fields: [
          { name: 'name', label: 'Name', type: 'text', required: true },
          { name: 'employeeId', label: 'Employee ID', type: 'text', required: true },
          { name: 'shift', label: 'Default Shift', type: 'select', options: ['Day', 'Night', 'Rotating'] },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'On Leave', 'Inactive'] },
        ]
      },
      { 
        id: 'products', 
        name: 'Products (FG Types)', 
        fields: [
          { name: 'name', label: 'Product Name', type: 'text', required: true },
          { name: 'code', label: 'Product Code', type: 'text' },
          { name: 'category', label: 'Category', type: 'select', options: ['Cubes', 'Notch Bar', 'Shots', 'Ingots'] },
          { name: 'unit', label: 'Unit', type: 'select', options: ['MT', 'KG', 'Pieces'] },
        ]
      },
    ]
  },
  'raw-material': {
    name: 'Raw Material',
    icon: Package,
    color: 'green',
    tables: [
      { 
        id: 'scrap_types', 
        name: 'Scrap Types', 
        fields: [
          { name: 'name', label: 'Scrap Type', type: 'text', required: true },
          { name: 'code', label: 'Code', type: 'text' },
          { name: 'category', label: 'Category', type: 'select', options: ['Aluminium', 'Ferrous', 'Non-Ferrous', 'Dross'] },
          { name: 'unit', label: 'Unit', type: 'select', options: ['KG', 'MT', 'Pieces'] },
        ]
      },
      { 
        id: 'suppliers', 
        name: 'Supplier Dashboard', 
        fields: [
          { name: 'name', label: 'Party Name', type: 'text', required: true },
          { name: 'code', label: 'Party Code', type: 'text' },
          { name: 'gst', label: 'GST No', type: 'text' },
          { name: 'contactName', label: 'Contact Name', type: 'text' },
          { name: 'contact', label: 'Contact Number', type: 'text' },
          { name: 'email', label: 'Contact Email', type: 'text' },
          { name: 'address', label: 'Address', type: 'text' },
          { name: 'city', label: 'City', type: 'text' },
          { name: 'state', label: 'State', type: 'text' },
          { name: 'country', label: 'Country', type: 'country' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive', 'Blacklisted'] },
        ]
      },
    ]
  },
  dispatch: {
    name: 'Dispatch',
    icon: Truck,
    color: 'purple',
    tables: [
      { 
        id: 'customers', 
        name: 'Customers', 
        fields: [
          { name: 'name', label: 'Customer Name', type: 'text', required: true },
          { name: 'code', label: 'Customer Code', type: 'text' },
          { name: 'gst', label: 'GST No', type: 'text' },
          { name: 'contact', label: 'Contact', type: 'text' },
          { name: 'address', label: 'Address', type: 'text' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
      { 
        id: 'vehicles', 
        name: 'Vehicles', 
        fields: [
          { name: 'vehicleNo', label: 'Vehicle No', type: 'text', required: true },
          { name: 'type', label: 'Type', type: 'select', options: ['Truck', 'Trailer', 'Container'] },
          { name: 'capacity', label: 'Capacity (MT)', type: 'number' },
          { name: 'owner', label: 'Owner/Transporter', type: 'text' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
      { 
        id: 'transporters', 
        name: 'Transporters', 
        fields: [
          { name: 'name', label: 'Transporter Name', type: 'text', required: true },
          { name: 'contact', label: 'Contact', type: 'text' },
          { name: 'address', label: 'Address', type: 'text' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
    ]
  },
  stores: {
    name: 'Stores',
    icon: Warehouse,
    color: 'amber',
    tables: [
      { 
        id: 'vendors', 
        name: 'Vendor Dashboard', 
        fields: [
          { name: 'vendorCode', label: 'Vendor Code', type: 'text', required: true },
          { name: 'vendorName', label: 'Vendor Name', type: 'text', required: true },
          { name: 'contactPerson', label: 'Contact Person', type: 'text' },
          { name: 'phone', label: 'Phone', type: 'text' },
          { name: 'email', label: 'Email', type: 'text' },
          { name: 'gstNo', label: 'GST No', type: 'text' },
          { name: 'panNo', label: 'PAN No', type: 'text' },
          { name: 'address', label: 'Address', type: 'text' },
          { name: 'city', label: 'City', type: 'text' },
          { name: 'state', label: 'State', type: 'text' },
          { name: 'country', label: 'Country', type: 'country' },
          { name: 'pincode', label: 'Pincode', type: 'text' },
          { name: 'bankName', label: 'Bank Name', type: 'text' },
          { name: 'accountNo', label: 'Account No', type: 'text' },
          { name: 'ifscCode', label: 'IFSC Code', type: 'text' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive', 'Blacklisted'] },
        ]
      },
      { 
        id: 'commodities', 
        name: 'Commodity, HSN & GST', 
        fields: [
          { name: 'commodityCode', label: 'Commodity Code', type: 'text', required: true },
          { name: 'commodityName', label: 'Commodity Name', type: 'text', required: true },
          { name: 'category', label: 'Category', type: 'select', options: ['Raw Material', 'Consumables', 'Spares', 'Packing Material', 'Safety Equipment', 'Tools', 'Chemicals', 'Others'] },
          { name: 'hsnCode', label: 'HSN Code', type: 'text', required: true },
          { name: 'gstRate', label: 'GST Rate (%)', type: 'number', required: true },
          { name: 'cgst', label: 'CGST (%)', type: 'number' },
          { name: 'sgst', label: 'SGST (%)', type: 'number' },
          { name: 'igst', label: 'IGST (%)', type: 'number' },
          { name: 'uom', label: 'Default UOM', type: 'text' },
          { name: 'description', label: 'Description', type: 'text' },
        ]
      },
      { 
        id: 'service_sac', 
        name: 'Services & SACs', 
        fields: [
          { name: 'sacCode', label: 'SAC Code', type: 'text', required: true },
          { name: 'sacDescription', label: 'Service Description', type: 'text', required: true },
          { name: 'gstRate', label: 'GST Rate (%)', type: 'number', required: true },
          { name: 'cgst', label: 'CGST (%)', type: 'number' },
          { name: 'sgst', label: 'SGST (%)', type: 'number' },
          { name: 'igst', label: 'IGST (%)', type: 'number' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
      { 
        id: 'terms_conditions', 
        name: 'Terms & Conditions', 
        fields: [
          { name: 'termCode', label: 'Term Code', type: 'text', required: true },
          { name: 'termType', label: 'Term Type', type: 'select', options: ['Purchase', 'Sale', 'General', 'Delivery', 'Warranty', 'Return'] },
          { name: 'termTitle', label: 'Title', type: 'text', required: true },
          { name: 'termDescription', label: 'Description', type: 'text', required: true },
          { name: 'applicableTo', label: 'Applicable To', type: 'select', options: ['All', 'Domestic', 'International', 'Specific Vendors'] },
          { name: 'priority', label: 'Display Priority', type: 'number' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
      { 
        id: 'payment_terms', 
        name: 'Payment Terms', 
        fields: [
          { name: 'paymentCode', label: 'Payment Code', type: 'text', required: true },
          { name: 'paymentName', label: 'Payment Term Name', type: 'text', required: true },
          { name: 'creditDays', label: 'Credit Days', type: 'number', required: true },
          { name: 'discountPercent', label: 'Early Payment Discount (%)', type: 'number' },
          { name: 'discountDays', label: 'Discount Valid Days', type: 'number' },
          { name: 'description', label: 'Description', type: 'text' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
      { 
        id: 'uom', 
        name: 'Unit of Measurement', 
        fields: [
          { name: 'uomCode', label: 'UOM Code', type: 'text', required: true },
          { name: 'uomName', label: 'UOM Name', type: 'text', required: true },
          { name: 'uomType', label: 'Type', type: 'select', options: ['Weight', 'Volume', 'Length', 'Area', 'Count', 'Time', 'Others'] },
          { name: 'baseUom', label: 'Base UOM', type: 'text' },
          { name: 'conversionFactor', label: 'Conversion Factor', type: 'number' },
          { name: 'description', label: 'Description', type: 'text' },
        ]
      },
      { 
        id: 'state_codes', 
        name: 'State Codes', 
        fields: [
          { name: 'stateCode', label: 'State Code', type: 'text', required: true },
          { name: 'stateName', label: 'State Name', type: 'text', required: true },
          { name: 'gstStateCode', label: 'GST State Code', type: 'text', required: true },
          { name: 'country', label: 'Country', type: 'country' },
          { name: 'zone', label: 'Zone', type: 'select', options: ['North', 'South', 'East', 'West', 'Central', 'North-East'] },
        ]
      },
    ]
  },
  projects: {
    name: 'Projects',
    icon: FolderKanban,
    color: 'indigo',
    tables: [
      { 
        id: 'project_types', 
        name: 'Project Types', 
        fields: [
          { name: 'name', label: 'Type Name', type: 'text', required: true },
          { name: 'description', label: 'Description', type: 'text' },
        ]
      },
      { 
        id: 'contractors', 
        name: 'Contractors', 
        fields: [
          { name: 'name', label: 'Contractor Name', type: 'text', required: true },
          { name: 'contact', label: 'Contact', type: 'text' },
          { name: 'specialization', label: 'Specialization', type: 'text' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
    ]
  },
  maintenance: {
    name: 'Maintenance',
    icon: Wrench,
    color: 'orange',
    tables: [
      { 
        id: 'equipment', 
        name: 'Equipment', 
        fields: [
          { name: 'name', label: 'Equipment Name', type: 'text', required: true },
          { name: 'code', label: 'Equipment Code', type: 'text' },
          { name: 'location', label: 'Location', type: 'text' },
          { name: 'category', label: 'Category', type: 'select', options: ['Electrical', 'Mechanical', 'Hydraulic', 'Pneumatic'] },
          { name: 'status', label: 'Status', type: 'select', options: ['Operational', 'Under Maintenance', 'Out of Service'] },
        ]
      },
      { 
        id: 'maintenance_types', 
        name: 'Maintenance Types', 
        fields: [
          { name: 'name', label: 'Type Name', type: 'text', required: true },
          { name: 'frequency', label: 'Frequency', type: 'select', options: ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly', 'As Needed'] },
          { name: 'description', label: 'Description', type: 'text' },
        ]
      },
    ]
  },
  hr: {
    name: 'HR',
    icon: UserCog,
    color: 'pink',
    tables: [
      { 
        id: 'departments', 
        name: 'Departments', 
        fields: [
          { name: 'name', label: 'Department Name', type: 'text', required: true },
          { name: 'code', label: 'Dept Code', type: 'text' },
          { name: 'head', label: 'Department Head', type: 'text' },
        ]
      },
      { 
        id: 'designations', 
        name: 'Designations', 
        fields: [
          { name: 'name', label: 'Designation', type: 'text', required: true },
          { name: 'level', label: 'Level', type: 'select', options: ['Executive', 'Manager', 'Supervisor', 'Worker', 'Trainee'] },
          { name: 'department', label: 'Department', type: 'text' },
        ]
      },
      { 
        id: 'leave_types', 
        name: 'Leave Types', 
        fields: [
          { name: 'name', label: 'Leave Type', type: 'text', required: true },
          { name: 'days', label: 'Days Allowed', type: 'number' },
          { name: 'carryForward', label: 'Carry Forward', type: 'select', options: ['Yes', 'No'] },
        ]
      },
    ]
  },
  imports: {
    name: 'Imports',
    icon: Ship,
    color: 'cyan',
    tables: [
      { 
        id: 'ports', 
        name: 'Ports', 
        fields: [
          { name: 'name', label: 'Port Name', type: 'text', required: true },
          { name: 'country', label: 'Country', type: 'country' },
          { name: 'code', label: 'Port Code', type: 'text' },
        ]
      },
      { 
        id: 'shipping_lines', 
        name: 'Shipping Lines', 
        fields: [
          { name: 'name', label: 'Shipping Line', type: 'text', required: true },
          { name: 'contact', label: 'Contact', type: 'text' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
      { 
        id: 'clearing_agents', 
        name: 'Clearing Agents', 
        fields: [
          { name: 'name', label: 'Agent Name', type: 'text', required: true },
          { name: 'contact', label: 'Contact', type: 'text' },
          { name: 'port', label: 'Port', type: 'text' },
          { name: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        ]
      },
    ]
  },
};

function MasterData() {
  const { department } = useParams();
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [activeTable, setActiveTable] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [newRecord, setNewRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState(null);
  const [projectRefresh, setProjectRefresh] = useState(0);
  const isProjectMaster = department === 'projects' && activeTable === 'project_types';

  // Replace-master modal state
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [replaceResult, setReplaceResult] = useState(null);  // success summary
  const [replaceError, setReplaceError] = useState(null);    // error detail

  const submitReplace = async () => {
    if (!replaceFile) return;
    setReplaceBusy(true);
    setReplaceError(null);
    setReplaceResult(null);
    try {
      const endpoint = activeTable === 'commodities'
        ? `${API}/stores/commodity-master/replace`
        : `${API}/stores/service-master/replace`;
      const fd = new FormData();
      fd.append('file', replaceFile);
      const res = await axios.post(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setReplaceResult(res.data);
      // Refresh table
      await fetchRecords();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setReplaceError(detail);
      } else {
        setReplaceError({ message: typeof detail === 'string' ? detail : err.message });
      }
    } finally {
      setReplaceBusy(false);
    }
  };

  const closeReplaceModal = () => {
    if (replaceBusy) return;
    setShowReplaceModal(false);
    setReplaceFile(null);
    setReplaceResult(null);
    setReplaceError(null);
  };

  const config = departmentConfig[department];
  
  useEffect(() => {
    if (config?.tables?.length > 0) {
      setActiveTable(config.tables[0].id);
    }
  }, [department]);

  useEffect(() => {
    if (activeTable) {
      fetchRecords();
    }
  }, [activeTable]);

  const fetchRecords = async () => {
    if (isProjectMaster) { setProjectRefresh((v) => v + 1); return; }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/master-data/${department}/${activeTable}`);
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching records:', error);
      setRecords([]);
    }
    setLoading(false);
  };

  const saveRecord = async (record, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/master-data/${department}/${activeTable}`, record);
        setMessage({ type: 'success', text: 'Record added successfully!' });
      } else {
        await axios.put(`${API}/master-data/${department}/${activeTable}/${record.id}`, record);
        setMessage({ type: 'success', text: 'Record updated successfully!' });
      }
      setEditingRecord(null);
      setNewRecord(null);
      fetchRecords();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save record' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const deleteRecord = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    try {
      await axios.delete(`${API}/master-data/${department}/${activeTable}/${id}`);
      setMessage({ type: 'success', text: 'Record deleted successfully!' });
      fetchRecords();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete record' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const currentTableConfig = config?.tables?.find(t => t.id === activeTable);
  
  const filteredRecords = records.filter(r => {
    if (!searchTerm) return true;
    return Object.values(r).some(v => 
      String(v).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (!config) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
        <h2 className="text-xl font-bold text-gray-800">Department not found</h2>
        <p className="text-gray-500">The requested department "{department}" doesn't exist.</p>
      </div>
    );
  }

  const Icon = config.icon;
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    amber: 'from-amber-500 to-amber-600',
    indigo: 'from-indigo-500 to-indigo-600',
    orange: 'from-orange-500 to-orange-600',
    pink: 'from-pink-500 to-pink-600',
    cyan: 'from-cyan-500 to-cyan-600',
  };

  return (
    <div className="space-y-6" data-testid="master-data-page">
      {/* Header */}
      <div className="flex flex-wrap gap-3 items-center justify-between bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className={`p-2 bg-gradient-to-br ${colorClasses[config.color]} rounded-lg`}>
            <Icon className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{config.name} Master Data</h1>
            <p className="text-sm text-gray-500">Manage master data for {config.name} department</p>
          </div>
        </div>
        <button
          onClick={fetchRecords}
          data-testid="master-data-refresh"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Table Selection Tabs */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <label className="block text-xs font-medium text-gray-500 mb-2">Select Data Type</label>
        <div className="flex flex-wrap gap-2">
          {config.tables.map(table => (
            <button
              key={table.id}
              onClick={() => { setActiveTable(table.id); setNewRecord(null); setEditingRecord(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTable === table.id
                  ? `bg-gradient-to-r ${colorClasses[config.color]} text-white shadow-md`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`table-${table.id}-btn`}
            >
              {table.name}
            </button>
          ))}
          
          {/* Consumption & Stock tab for Raw Material */}
          {department === 'raw-material' && (
            <button
              onClick={() => { setActiveTable('consumption_stock'); setNewRecord(null); setEditingRecord(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTable === 'consumption_stock'
                  ? `bg-gradient-to-r ${colorClasses[config.color]} text-white shadow-md`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="consumption-stock-btn"
            >
              Consumption & Stock
            </button>
          )}
        </div>
      </div>

      {/* Consumption & Stock Tab Content */}
      {activeTable === 'consumption_stock' && department === 'raw-material' && (
        <RawMaterialConsumptionStock />
      )}

      {/* Search & Add - Only show for standard tables */}
      {isProjectMaster && <ProjectMasterData refreshKey={projectRefresh} />}
      {currentTableConfig && !isProjectMaster && activeTable !== 'consumption_stock' && (
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={`Search ${currentTableConfig.name}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              {department === 'stores' && (activeTable === 'commodities' || activeTable === 'service_sac') && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      const url = `${API}/stores/commodity-export.xlsx`;
                      window.open(url, '_blank');
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition border border-amber-200 text-sm font-medium"
                    title="Download Excel of every commodity/service used across Stores + Project POs, with drift flags. Use this to rebuild the master."
                    data-testid="rebuild-master-from-pos-btn"
                  >
                    <Download size={16} />
                    Rebuild from POs
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReplaceModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-rose-100 text-rose-800 rounded-lg hover:bg-rose-200 transition border border-rose-200 text-sm font-medium"
                    title="Upload your edited Excel to REPLACE the entire master (old data is backed up first)."
                    data-testid="replace-master-btn"
                  >
                    <Upload size={16} />
                    Replace from Excel
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  const emptyRecord = {};
                  currentTableConfig.fields.forEach(f => emptyRecord[f.name] = '');
                  setNewRecord(emptyRecord);
                  setEditingRecord(null);
                }}
                className={`flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r ${colorClasses[config.color]} text-white rounded-lg hover:opacity-90 transition shadow-md`}
                data-testid="add-record-btn"
              >
                <Plus size={18} />
                Add {currentTableConfig.name.replace(/s$/, '')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Record Form */}
      {newRecord && currentTableConfig && !isProjectMaster && activeTable !== 'consumption_stock' && (
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Add New {currentTableConfig.name.replace(/s$/, '')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {currentTableConfig.fields.map(field => (
              <div key={field.name}>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                {field.type === 'select' ? (
                  <select
                    value={newRecord[field.name] || ''}
                    onChange={(e) => setNewRecord({ ...newRecord, [field.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  >
                    <option value="">Select...</option>
                    {field.options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === 'country' ? (
                  <CountryDropdown
                    value={newRecord[field.name] || ''}
                    onChange={(value) => setNewRecord({ ...newRecord, [field.name]: value })}
                    placeholder="Select country..."
                  />
                ) : (
                  <input
                    type={field.type}
                    value={newRecord[field.name] || ''}
                    onChange={(e) => setNewRecord({ ...newRecord, [field.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => saveRecord(newRecord, true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Save size={16} />
              Save
            </button>
            <button
              onClick={() => setNewRecord(null)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Records Table - Only show for standard tables */}
      {currentTableConfig && !isProjectMaster && activeTable !== 'consumption_stock' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-800">{currentTableConfig.name}</h3>
            <p className="text-sm text-gray-500">{filteredRecords.length} records</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                  {currentTableConfig.fields.map(field => (
                    <th key={field.name} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      {field.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={currentTableConfig.fields.length + 2} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                      Loading...
                    </td>
                  </tr>
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={currentTableConfig.fields.length + 2} className="px-4 py-8 text-center text-gray-500">
                      <Database className="mx-auto mb-2 text-gray-300" size={32} />
                      No records found. Click "Add" to create one.
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record, idx) => {
                    // Use sacCode as fallback ID for service_sac records
                    const recordId = record.id || record.sacCode || `row-${idx}`;
                    return (
                    <tr key={recordId} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                      {currentTableConfig.fields.map(field => (
                        <td key={field.name} className="px-4 py-3">
                          {editingRecord?.id === recordId ? (
                            field.type === 'select' ? (
                              <select
                                value={editingRecord[field.name] || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, [field.name]: e.target.value })}
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:border-blue-400 outline-none text-sm"
                              >
                                <option value="">Select...</option>
                                {field.options.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : field.type === 'country' ? (
                              <CountryDropdown
                                value={editingRecord[field.name] || ''}
                                onChange={(value) => setEditingRecord({ ...editingRecord, [field.name]: value })}
                                placeholder="Select..."
                              />
                            ) : (
                              <input
                                type={field.type}
                                value={editingRecord[field.name] || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, [field.name]: e.target.value })}
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:border-blue-400 outline-none text-sm"
                              />
                            )
                          ) : (
                            <span className="text-sm text-gray-800">{record[field.name] || '-'}</span>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-center">
                        {editingRecord?.id === recordId ? (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => saveRecord(editingRecord)}
                              className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200 transition"
                              title="Save"
                            >
                              <Save size={14} />
                            </button>
                            <button
                              onClick={() => setEditingRecord(null)}
                              className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition"
                              title="Cancel"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setEditingRecord({ ...record, id: recordId })}
                              className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition"
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => deleteRecord(recordId)}
                              className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )})
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Replace-from-Excel Modal */}
      {showReplaceModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="replace-master-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-rose-50 rounded-t-2xl">
              <div>
                <h2 className="text-lg font-semibold text-rose-900 flex items-center gap-2">
                  <Upload size={18} />
                  Replace {activeTable === 'commodities' ? 'Commodity Master' : 'Service & SAC Master'} from Excel
                </h2>
                <p className="text-xs text-rose-700 mt-1">
                  Existing records will be backed up server-side before being wiped.
                </p>
              </div>
              <button
                type="button"
                onClick={closeReplaceModal}
                disabled={replaceBusy}
                className="p-2 hover:bg-rose-100 rounded-lg transition disabled:opacity-50"
                data-testid="replace-modal-close-btn"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Pre-state: file picker + warning */}
              {!replaceResult && !replaceError && (
                <>
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                    <p className="font-semibold mb-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      What will happen on submit
                    </p>
                    <ol className="list-decimal list-inside space-y-0.5 ml-1">
                      <li>All <strong>{records.length}</strong> existing records will be copied to a timestamped backup collection.</li>
                      <li>The live master will be wiped.</li>
                      <li>New rows from your Excel will be inserted.</li>
                      <li>The backup can be restored later if anything looks wrong.</li>
                    </ol>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600 uppercase">
                      Excel file (.xlsx)
                    </label>
                    <p className="text-[11px] text-gray-500 mb-2">
                      Must contain the sheet <code className="bg-gray-100 px-1 rounded">
                        {activeTable === 'commodities' ? 'Materials (HSN)' : 'Services (SAC)'}
                      </code> with columns:&nbsp;
                      <em>Commodity Name</em>,&nbsp;
                      <em>{activeTable === 'commodities' ? 'HSN' : 'SAC'} Code</em>,&nbsp;
                      <em>GST %</em>
                      &nbsp;(optionally <em>{activeTable === 'commodities' ? 'HSN' : 'SAC'} Description</em>).
                    </p>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
                      className="w-full text-sm border border-gray-300 rounded-lg p-2 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-rose-100 file:text-rose-700 file:font-medium hover:file:bg-rose-200"
                      data-testid="replace-file-input"
                    />
                    {replaceFile && (
                      <p className="text-xs text-gray-700 mt-2">
                        Selected: <span className="font-medium">{replaceFile.name}</span>
                        &nbsp;({(replaceFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Error state: validation failures */}
              {replaceError && (
                <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
                  <p className="font-semibold mb-2 flex items-center gap-1">
                    <AlertCircle size={14} />
                    Upload failed — please fix the file and try again
                  </p>
                  <p className="mb-2">
                    {typeof replaceError.message === 'string' ? replaceError.message : 'Validation error'}
                  </p>
                  {Array.isArray(replaceError.conflicts) && replaceError.conflicts.length > 0 && (
                    <div className="mt-2 bg-white border border-rose-200 rounded p-2 max-h-48 overflow-y-auto">
                      <p className="text-xs font-semibold mb-1">
                        Conflicts (showing first {replaceError.conflicts.length} of {replaceError.totalConflicts}):
                      </p>
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b border-rose-200">
                            <th className="text-left py-1">Commodity Name</th>
                            <th className="text-left py-1">Conflicting Codes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {replaceError.conflicts.map((c, i) => (
                            <tr key={i} className="border-b border-rose-100">
                              <td className="py-1 font-mono">{c.commodityName}</td>
                              <td className="py-1 font-mono">{(c.conflictingCodes || []).join(', ')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Success state */}
              {replaceResult && (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900">
                  <p className="font-semibold mb-2 flex items-center gap-1">
                    <CheckCircle size={14} />
                    Master replaced successfully
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white rounded p-2 border border-emerald-200">
                      <div className="text-gray-600">Backed up</div>
                      <div className="text-lg font-bold">{replaceResult.backedUp}</div>
                    </div>
                    <div className="bg-white rounded p-2 border border-emerald-200">
                      <div className="text-gray-600">Deleted</div>
                      <div className="text-lg font-bold">{replaceResult.deleted}</div>
                    </div>
                    <div className="bg-white rounded p-2 border border-emerald-200 col-span-2">
                      <div className="text-gray-600">Inserted (new master size)</div>
                      <div className="text-2xl font-bold text-emerald-800">{replaceResult.inserted}</div>
                    </div>
                  </div>
                  {replaceResult.skippedBlank > 0 && (
                    <p className="text-xs mt-2 text-emerald-700">
                      ⚠ Skipped {replaceResult.skippedBlank} blank/incomplete row(s).
                    </p>
                  )}
                  {replaceResult.skippedDuplicatePairs > 0 && (
                    <p className="text-xs mt-1 text-emerald-700">
                      ⚠ Skipped {replaceResult.skippedDuplicatePairs} exact-duplicate (name + code) row(s) — only the first occurrence was kept.
                    </p>
                  )}
                  <p className="text-[11px] mt-3 font-mono break-all text-emerald-700">
                    Backup collection:&nbsp;
                    <span className="bg-white px-1 rounded">{replaceResult.backupCollection}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeReplaceModal}
                disabled={replaceBusy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
                data-testid="replace-modal-cancel-btn"
              >
                {replaceResult ? 'Done' : 'Cancel'}
              </button>
              {!replaceResult && (
                <button
                  type="button"
                  onClick={submitReplace}
                  disabled={!replaceFile || replaceBusy}
                  className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition disabled:opacity-50 flex items-center gap-2"
                  data-testid="replace-modal-submit-btn"
                >
                  {replaceBusy ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Backing up & replacing...
                    </>
                  ) : (
                    <>
                      <Upload size={14} />
                      Replace Master
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterData;
